'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

export default function HeroSection() {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  return (
    <section className="relative w-full min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-secondary via-secondary/95 to-primary/20">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
          backgroundSize: '40px 40px'
        }}></div>
      </div>
      {/* Floating Icons */}
      <div className="absolute inset-0 pointer-events-none">
        <Icon name="CpuChipIcon" size={60} className="absolute top-20 left-10 text-primary/20 animate-pulse delay-100" />
        <Icon name="CodeBracketIcon" size={50} className="absolute top-40 right-20 text-primary/20 animate-pulse delay-300" />
        <Icon name="AcademicCapIcon" size={70} className="absolute bottom-32 left-20 text-primary/20 animate-pulse delay-500" />
        <Icon name="RocketLaunchIcon" size={55} className="absolute bottom-20 right-32 text-primary/20 animate-pulse delay-700" />
      </div>
      <div className="relative z-10 max-w-7xl mx-auto px-6 py-20 text-center">
        {/* Badge */}
        <div
          className={`inline-flex items-center gap-2 bg-white/10 backdrop-blur-md border border-white/20 rounded-full px-4 py-2 mb-8 ${
            isVisible ? 'animate-fadeInUp delay-100' : 'opacity-0'
          }`}
        >
          <Icon name="SparklesIcon" size={16} className="text-primary" />
          <span className="text-xs font-semibold uppercase tracking-wider text-white">
            Established 2020 • 1000+ Students Trained
          </span>
        </div>

        {/* Main Heading */}
        <h1
          className={`font-display text-6xl md:text-8xl lg:text-9xl uppercase tracking-tighter leading-none text-white mb-6 ${
            isVisible ? 'animate-slideInBlur delay-200' : 'opacity-0'
          }`}
        >
          Upgrade Your<br />
          <span className="text-primary">Skills.</span> Build<br />
          Your Future.
        </h1>

        {/* Subheadline */}
        <p
          className={`text-lg md:text-xl text-white/80 max-w-2xl mx-auto mb-10 leading-relaxed ${
            isVisible ? 'animate-fadeInUp delay-400' : 'opacity-0'
          }`}
        >
          Learn Industry-Ready Skills with Practical Training. From Learning to Earning – Start with Apollobits Academy.
        </p>

        {/* CTA Buttons */}
        <div
          className={`flex flex-col sm:flex-row gap-4 justify-center items-center ${
            isVisible ? 'animate-fadeInScale delay-600' : 'opacity-0'
          }`}
        >
          <Link
            href="/contact"
            className="group relative inline-flex items-center gap-2 bg-primary text-primary-foreground px-8 py-4 rounded-full text-base font-semibold uppercase tracking-wider hover:bg-primary/90 transition-all shadow-xl hover:shadow-2xl hover:scale-105"
          >
            Enroll Now
            <Icon name="ArrowRightIcon" size={20} className="group-hover:translate-x-1 transition-transform" />
          </Link>
          <Link
            href="/courses"
            className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md text-white border border-white/30 px-8 py-4 rounded-full text-base font-semibold uppercase tracking-wider hover:bg-white/20 transition-all"
          >
            <Icon name="PlayIcon" size={20} />
            Get Free Demo
          </Link>
        </div>

        {/* Stats */}
        <div
          className={`grid grid-cols-2 md:grid-cols-4 gap-8 mt-16 max-w-4xl mx-auto ${
            isVisible ? 'animate-fadeInUp delay-800' : 'opacity-0'
          }`}
        >
          {[
            { label: 'Years Experience', value: '5+' },
            { label: 'Students Trained', value: '1000+' },
            { label: 'Courses Offered', value: '7+' },
            { label: 'Expert Trainers', value: '10+' },
          ]?.map((stat, index) => (
            <div key={`stat_${index}`} className="text-center">
              <div className="text-4xl md:text-5xl font-display text-primary mb-2">
                {stat?.value}
              </div>
              <div className="text-xs uppercase tracking-wider text-white/60">
                {stat?.label}
              </div>
            </div>
          ))}
        </div>

        {/* Scroll Indicator */}
        <div className={`mt-16 ${isVisible ? 'animate-fadeInUp delay-1000' : 'opacity-0'}`}>
          <div className="w-10 h-10 rounded-full border-2 border-white/30 flex items-center justify-center mx-auto animate-bounce">
            <Icon name="ChevronDownIcon" size={20} className="text-white" />
          </div>
        </div>
      </div>
    </section>
  );
}